/// <amd-module name="Reporting/CFDReport/CfdReportUserSettings" />
import { IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { CumulativeFlowDiagramSettings } from "Widgets/CumulativeFlowDiagram/CumulativeFlowDiagramContracts";

/* Contract for the user preferences stored in the platform settings service*/
export interface CFDReportUserSettingsData {
    rollingDuration: number;
    boardLane: string;
    boardColumnIds: string[];
}

/* Encapsulate state and behavior for the user preferences stored in and retrieved from the platform settings service*/
export class CFDReportUserSettings {
    public static daysKey = "Days";
    public static boardLaneKey = "BoardLane";
    public static boardColumnsKey = "BoardColumnIds";
    public static teamScopeName = "team";

    public static async reset(pageContext: IVssPageContext, teamId: string, backlogCategory: string): Promise<void> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const reportKey = getCFDReportSettingsStorageKey();
        await settingsService.removeEntries(reportKey, SettingsUserScope.Me, CFDReportUserSettings.teamScopeName, teamId);
    }

    public static async save(
        pageContext: IVssPageContext,
        newSettings: CumulativeFlowDiagramSettings,
        teamId: string,
        backlogCategory: string
    ): Promise<void> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const reportKey = getCFDReportSettingsStorageKey();
        const entryPrefix = `${reportKey}/${backlogCategory}`;

        const userSettings: {
            [key: string]: any;
        } = {};
        userSettings[entryPrefix + "/" + CFDReportUserSettings.daysKey] = newSettings.chartDataSettings.timePeriod.settings;
        userSettings[entryPrefix + "/" + CFDReportUserSettings.boardLaneKey] = newSettings.chartDataSettings.boardLane;
        userSettings[entryPrefix + "/" + CFDReportUserSettings.boardColumnsKey] = newSettings.chartDataSettings.desiredBoardColumnIds;

        await settingsService.setEntries(userSettings, SettingsUserScope.Me, CFDReportUserSettings.teamScopeName, teamId);
    }

    public static async load(pageContext: IVssPageContext, teamId: string, backlogCategory: string): Promise<CFDReportUserSettingsData> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");

        const userSettings: {
            [key: string]: any;
        } = await settingsService.getEntriesAsync(
            getCFDReportSettingsStorageKey() + "/" + backlogCategory,
            SettingsUserScope.Me,
            CFDReportUserSettings.teamScopeName,
            teamId
        );
        return {
            rollingDuration: userSettings[CFDReportUserSettings.daysKey],
            boardLane: userSettings[CFDReportUserSettings.boardLaneKey],
            boardColumnIds: userSettings[CFDReportUserSettings.boardColumnsKey]
        } as CFDReportUserSettingsData;
    }
}

export function getCFDReportSettingsStorageKey(): string {
    return "CFDReport";
}
